// Pan/zoom of the plan. The plan image and the SVG overlay live inside #world,
// which is moved with a single CSS transform. All marker/shape coordinates are
// stored in original-image pixels, so zoom and window size never touch the data.
import { getState } from "./state";

interface ViewEls {
  viewport: HTMLElement;
  world: HTMLElement;
  planImg: HTMLImageElement;
  overlay: SVGSVGElement;
  zoomPct: HTMLElement;
  scaleLabel: HTMLElement;
  scaleBarInner: HTMLElement;
}

let els: ViewEls;
let imgW = 1;
let imgH = 1;
let zoom = 1;
let tx = 0;
let ty = 0;
let fitZoom = 1;
let minZoom = 0.1;
let maxZoom = 10;

const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

export function initView(elements: ViewEls): void {
  els = elements;
}

export function setImage(url: string, w: number, h: number): void {
  imgW = w;
  imgH = h;
  els.planImg.width = w;
  els.planImg.height = h;
  els.planImg.src = url;
  els.world.style.width = `${w}px`;
  els.world.style.height = `${h}px`;
  els.overlay.setAttribute("viewBox", `0 0 ${w} ${h}`);
  els.overlay.setAttribute("width", String(w));
  els.overlay.setAttribute("height", String(h));
}

export function imageSize(): { w: number; h: number } {
  return { w: imgW, h: imgH };
}

export function getZoom(): number {
  return zoom;
}

export function applyTransform(): void {
  els.world.style.transform = `translate(${tx}px,${ty}px) scale(${zoom})`;
  els.zoomPct.textContent = `${Math.round((zoom / fitZoom) * 100)}%`;
  updateScaleBar();
}

export function fitView(): void {
  const vw = els.viewport.clientWidth;
  const vh = els.viewport.clientHeight;
  if (vw === 0 || vh === 0) return;
  fitZoom = Math.min(vw / imgW, vh / imgH) * 0.97;
  minZoom = fitZoom * 0.5;
  maxZoom = fitZoom * 14;
  zoom = fitZoom;
  tx = (vw - imgW * zoom) / 2;
  ty = (vh - imgH * zoom) / 2;
  applyTransform();
}

export function clientToImage(clientX: number, clientY: number): { x: number; y: number } {
  const r = els.viewport.getBoundingClientRect();
  return { x: (clientX - r.left - tx) / zoom, y: (clientY - r.top - ty) / zoom };
}

export function zoomAt(clientX: number, clientY: number, factor: number): void {
  const r = els.viewport.getBoundingClientRect();
  const cx = clientX - r.left;
  const cy = clientY - r.top;
  const p = clientToImage(clientX, clientY);
  zoom = clamp(zoom * factor, minZoom, maxZoom);
  tx = cx - p.x * zoom;
  ty = cy - p.y * zoom;
  applyTransform();
}

export function zoomByButton(factor: number): void {
  const r = els.viewport.getBoundingClientRect();
  zoomAt(r.left + els.viewport.clientWidth / 2, r.top + els.viewport.clientHeight / 2, factor);
}

export function panBy(dx: number, dy: number): void {
  tx += dx;
  ty += dy;
  applyTransform();
}

export function setPan(x: number, y: number): void {
  tx = x;
  ty = y;
  applyTransform();
}

export function panState(): { tx: number; ty: number } {
  return { tx, ty };
}

export function centerOn(x: number, y: number): void {
  const vw = els.viewport.clientWidth;
  const vh = els.viewport.clientHeight;
  tx = vw / 2 - x * zoom;
  ty = vh / 2 - y * zoom;
  applyTransform();
}

export function updateScaleBar(): void {
  const pxPerM = getState().scale.pxPerMeter * zoom;
  const options = [0.5, 1, 2, 5, 10, 20, 25, 50, 100, 200];
  let best = options[0]!;
  for (const opt of options) {
    if (opt * pxPerM <= 150) best = opt;
  }
  els.scaleLabel.textContent = `${best >= 1 ? best : String(best).replace(".", ",")} m`;
  els.scaleBarInner.style.width = `${best * pxPerM}px`;
}
